import { FormEvent, useMemo, useState } from 'react';
import './TicketChat.css';

type TicketChatUser = { id?: number; name?: string };
type TicketChatComment = {
  id: number;
  content: string;
  user?: TicketChatUser;
  created_at?: string;
};

type TicketChatProps = {
  comments: TicketChatComment[];
  currentUserId?: number;
  onSend: (message: string) => Promise<void> | void;
  sending?: boolean;
  error?: string | null;
};


export default function TicketChat({
  comments,
  currentUserId,
  onSend,
  sending = false,
  error = null,
}: TicketChatProps) {
  const [draft, setDraft] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateA - dateB;
    });
  }, [comments]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;

    try {
      setLocalError(null);
      await onSend(trimmed);
      setDraft('');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Impossible d'envoyer le message.");
    }
  };

  return (
    <div className="ticket-chat">
      <div className="ticket-chat__list">
        {sortedComments.length === 0 && (
          <p className="ticket-chat__empty">Aucun échange pour le moment.</p>
        )}
        {sortedComments.map((comment) => {
          const isSelf = currentUserId != null && comment.user?.id === currentUserId;

          return (
            <div
              key={comment.id}
              className={`ticket-chat__item ${
                isSelf ? 'ticket-chat__item--self' : 'ticket-chat__item--other'
              }`}
            >
              <div className="ticket-chat__bubble">
                {comment.content
                  ? comment.content.split(/\r?\n/).map((line, index, arr) => (
                      <span key={index}>
                        {line}
                        {index < arr.length - 1 && <br />}
                      </span>
                    ))
                  : ''}
              </div>
              <div className="ticket-chat__meta">
                <span className="ticket-chat__author">
                  {comment.user?.name ?? 'Utilisateur'}
                </span>
                {comment.created_at && (
                  <span className="ticket-chat__date">
                    {new Date(comment.created_at).toLocaleString('fr-FR')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <form className="ticket-chat__form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Écrire un message…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={sending}
          className="ticket-chat__input"
        />
        <button
          type="submit"
          className="ticket-chat__send"
          disabled={sending || !draft.trim()}
        >
          {sending ? 'Envoi…' : 'Envoyer'}
        </button>
      </form>

      {(error || localError) && (
        <p className="ticket-chat__error">{error ?? localError}</p>
      )}
    </div>
  );
}